import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Clock, Users, ChefHat, Flame, ShoppingCart } from 'lucide-react';

const difficultyColor = { 'Easy': '#50B86C', 'Medium': '#D9A34A', 'Hard': '#D94A6B' };

const RecipePage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Recipe', color: t.accent };

  const renderRecipeCard = () => {
    const ingredients = page.ingredients || [];
    const steps = page.steps || [];
    const md = page.metadata || {};
    const dietary = page.dietaryInfo || [];

    return (
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Metadata bar */}
        {(md.servings || md.prepTime || md.cookTime || md.difficulty) && (
          <div className="flex items-center gap-3 flex-wrap mb-2 pb-2" style={{ borderBottom: `1px solid ${t.accent}15` }}>
            {md.servings && (
              <div className="flex items-center gap-1">
                <Users className="w-2.5 h-2.5" style={{ color: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>{md.servings}</span>
              </div>
            )}
            {md.prepTime && (
              <div className="flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" style={{ color: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>Prep: {md.prepTime}</span>
              </div>
            )}
            {md.cookTime && (
              <div className="flex items-center gap-1">
                <Flame className="w-2.5 h-2.5" style={{ color: '#D97B4A' }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>Cook: {md.cookTime}</span>
              </div>
            )}
            {md.difficulty && (
              <span className="text-[8px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: (difficultyColor[md.difficulty] || t.accent) + '15', color: difficultyColor[md.difficulty] || t.accent }}>{md.difficulty}</span>
            )}
          </div>
        )}

        {page.content && (
          <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>
        )}

        <div className="flex gap-3 flex-1 min-h-0">
          {/* Left: Ingredients */}
          {ingredients.length > 0 && (
            <div className="w-[38%] flex-shrink-0">
              <h3 className="text-[9px] font-bold tracking-[0.12em] uppercase mb-1.5" style={{ fontFamily: t.bodyFont, color: t.accent }}>
                <ChefHat className="w-2.5 h-2.5 inline mr-1" style={{ color: t.accent }} />
                Ingredients
              </h3>
              <div className="space-y-0.5">
                {ingredients.map((ing, i) => (
                  <div key={i} className="flex items-baseline gap-1 py-0.5" style={{ borderBottom: `1px dotted ${t.fieldBorder || t.accent + '15'}` }}>
                    <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent, minWidth: '2.5rem' }}>
                      {ing.amount} {ing.unit}
                    </span>
                    <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text }}>{ing.item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Right: Steps */}
          {steps.length > 0 && (
            <div className="flex-1 min-w-0">
              <h3 className="text-[9px] font-bold tracking-[0.12em] uppercase mb-1.5" style={{ fontFamily: t.bodyFont, color: t.accent }}>Instructions</h3>
              <div className="space-y-1">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-1.5">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: t.accent + '15' }}>
                      <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', fontWeight: 700, color: t.accent }}>{i + 1}</span>
                    </div>
                    <p style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text, lineHeight: 1.5, flex: 1 }}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dietary info */}
        {dietary.length > 0 && (
          <div className="flex items-center gap-1 mt-2 pt-1.5" style={{ borderTop: `1px solid ${t.accent}10` }}>
            {dietary.map((d, i) => (
              <span key={i} className="text-[7px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#50B86C15', color: '#50B86C' }}>{d}</span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = () => (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: t.accent + '15' }}>
        <ChefHat className="w-7 h-7" style={{ color: t.accent }} strokeWidth={1.5} />
      </div>
      <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: t.text }}>{page.title}</h2>
      {page.content && <p className="mt-2" style={{ fontFamily: t.bodyFont, fontSize: '0.8rem', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
    </div>
  );

  const renderShopping = () => {
    const items = page.shoppingItems || [];
    return (
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingCart className="w-3.5 h-3.5" style={{ color: t.accent }} />
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', fontWeight: 600, color: t.text }}>Shopping List</span>
        </div>
        {items.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {items.map((cat, ci) => (
              <div key={ci}>
                <h4 className="text-[8px] font-bold uppercase tracking-wider mb-1" style={{ color: t.accent }}>{cat.category}</h4>
                <div className="space-y-0.5">
                  {(cat.items || []).map((item, ii) => (
                    <div key={ii} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
                      <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Fallback: render fields as simple checklist */
          <div className="space-y-1">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {page.fields.map((field, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.text, fontWeight: 500 }}>{field}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'recipe-card': renderRecipeCard,
    'recipe-category': renderCategory,
    'recipe-shopping': renderShopping,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <span className="inline-block text-[8px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full mb-2" style={{ fontFamily: t.bodyFont, backgroundColor: meta.color + '15', color: meta.color }}>{meta.label}</span>
          {page.pageType !== 'recipe-category' && (
            <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(0.95rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
          )}
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {render()}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default RecipePage;
